import { Bot, ExternalLink, KeyRound, Settings, Terminal } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar/sidebar-menu';
import { useSidebar } from '@/components/ui/sidebar/hooks/use-sidebar';
import { sidebarIconButton } from '@/components/ui/sidebar/helpers/sidebar-theme';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DOCS_API_OVERVIEW_URL, DOCS_CLI_INSTALL_URL } from '@/lib/constants/external-urls-constants';
import {
  getActionsForSurface,
} from '@/lib/navigation/account-action-catalog';
import {
  AccountActionId,
  AccountMenuSurface,
} from '@/lib/navigation/types/account-action-catalog-enums';
import { useResolvedAccountActionCatalog } from '@/hooks/navigation/use-resolved-account-action-catalog';

interface ApiKeysLinkProps {
  isCollapsed: boolean;
}

function DeveloperMenuBody() {
  const catalog = useResolvedAccountActionCatalog();
  const developerActions = getActionsForSurface(
    catalog,
    AccountMenuSurface.DESKTOP_DEVELOPER
  );
  const apiKeysAction = developerActions.find(a => a.id === AccountActionId.API_KEYS);

  return (
    <>
      <DropdownMenuLabel className="px-2 py-1 text-xs font-medium text-content-secondary">
        Developer
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        {apiKeysAction?.href ? (
          <DropdownMenuItem asChild className="gap-2 px-2 py-1.5 type-body">
            <Link to={apiKeysAction.href}>
              <Settings className="size-4 shrink-0" />
              {apiKeysAction.label}
            </Link>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-content-secondary">
        <Bot className="size-3.5" />
        Connect AI Agents
      </DropdownMenuLabel>
      <DropdownMenuGroup>
        <DropdownMenuItem asChild className="gap-2 px-2 py-1.5 type-body">
          <a href={DOCS_CLI_INSTALL_URL} rel="noopener noreferrer" target="_blank">
            <Terminal className="size-4 shrink-0" />
            CLI Setup Guide
            <ExternalLink className="ml-auto size-3 opacity-50" />
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="gap-2 px-2 py-1.5 type-body">
          <a href={DOCS_API_OVERVIEW_URL} rel="noopener noreferrer" target="_blank">
            <KeyRound className="size-4 shrink-0" />
            API Reference
            <ExternalLink className="ml-auto size-3 opacity-50" />
          </a>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  );
}

export function ApiKeysLink({ isCollapsed }: ApiKeysLinkProps) {
  const { isMobile } = useSidebar();

  if (isCollapsed) {
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Developers"
                className={sidebarIconButton()}
              >
                <KeyRound className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">Developers</TooltipContent>
        </Tooltip>
        <DropdownMenuContent
          align="start"
          className="w-56 rounded-lg p-1"
          side="right"
          sideOffset={8}
        >
          <DeveloperMenuBody />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              aria-label="Developers"
              className="h-8 gap-2 px-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <KeyRound className="size-4 shrink-0" />
              <span className="type-body font-medium">Developers</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 rounded-lg p-1"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DeveloperMenuBody />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
